import {makeAutoObservable, runInAction} from 'mobx';
import type { ReliefPreview, ReliefConfirmResponse, ReliefSource } from '@/shared/types/ReliefTypes';
import type { RecoveryPreview, RecoveryAcceptResponse } from '@/shared/types/RecoveryTypes';
import type {CreateOrderUseCase} from '@/core/application/use-cases/order/CreateOrderUseCase';
import type {GetOrdersUseCase} from '@/core/application/use-cases/order/GetOrdersUseCase';
import type {CreateCheckoutSessionUseCase} from '@/core/application/use-cases/checkout/CreateCheckoutSessionUseCase';
import type {
    InitializePaymentParams,
    InitializePaymentUseCase
} from '@/core/application/use-cases/payment/InitializePaymentUseCase';
import type {ApiPaymentService, UpcomingPayment} from '@/core/infrastructure/services/ApiPaymentService';
import {CheckoutSessionResult, CheckoutSessionService} from '@/core/infrastructure/services/ApiPaymentService';
import {OrderStatus, type Order, type PaymentType} from '@/core/domain/entities/Order';
import type { CreateOrderRequest, OrderFiltersRequest } from '@/core/application/dtos/OrderDto';
import { trackConsumerEvent } from '@/libs/analytics/consumerEvents';

/**
 * Cancellation preview response from the backend.
 * Returned by GET /v1/orders/{id}/cancellation-preview
 */
export interface CancellationPreview {
  eligible: boolean;
  kind: 'reversal' | 'cancellation';
  currency: string;
  total_paid: string;
  cancellation_fee_percent: string;
  cancellation_fee_amount: string;
  cancellation_fee_applies: boolean;
  net_refund_amount: string;
  reversal_window_hours: number;
  within_reversal_window: boolean;
  allowed_refund_types: string[];
  default_refund_type: string;
  reason_code?: string;
  detail?: string;
  /** Backend-provided refund timing string, e.g. "3-5 business days" */
  monetary_refund_expected_window?: string;
  /** Backend payment provider key, e.g. "mtn_mobile_money" */
  refund_provider?: string;
  /** Human-readable provider name, e.g. "MTN MoMo" */
  refund_provider_display?: string;
  /** Whether monetary refund is available for this order */
  monetary_refund_available?: boolean;
  /** SLA hours for cancellation review */
  cancellation_review_sla_hours?: number;
  /** Hold period in days before monetary refund is released */
  monetary_hold_days?: number;
  refund_required?: boolean;
  plan_started?: boolean;
  cancellation_cohort?: string;
}

/**
 * Cancellation request response from the backend.
 * Returned by POST /v1/orders/{id}/cancel
 */
export interface CancellationResponse {
  order: { id: string; status: string };
  cancellation: CancellationPreview & {
    requested_at: string;
    refund_type_preference?: 'store_credit' | 'monetary' | null;
    state: string;
  };
}

/**
 * Region data from the backend.
 * Returned by GET /v1/collection-points/regions
 */
export interface Region {
  code: string;
  label: string;
  has_collection_points: boolean;
}

/**
 * Collection point data from the backend.
 * Returned by GET /v1/collection-points?region={region}
 */
export interface CollectionPoint {
  id: string;
  code: string;
  name: string;
  region: string;
  city: string;
  address?: string;
  operating_hours?: unknown;
}

export interface LoadUserOrdersOptions {
    minimumVisibleCount?: number;
    visibilityMatcher?: (order: Order) => boolean;
    maxPages?: number;
}

export interface CheckoutSessionData {
    productId: string;
    quantity: number;
    paymentMethod: string;
    paymentPlan?: {
        durationMonths: number;
        frequency: string;
        kmpEnabled: boolean;
    };
    deliveryOption?: string;
    deliveryAddressId?: string;
}

/**
 * Payment status values returned by the backend API.
 *
 * Backend transforms InstallmentStatus (DUE, OVERDUE, FAILED) into these
 * time-based statuses in dashboard_service.py:
 * - "overdue": Payment is past due date
 * - "due_soon": Payment due within 7 days
 * - "scheduled": Payment due more than 7 days away
 *
 * Note: Backend already filters out PAID and WAIVED installments before
 * returning, so we only receive actionable payments.
 */
const _PAYMENT_STATUS = {
    OVERDUE: 'overdue',
    DUE_SOON: 'due_soon',
    SCHEDULED: 'scheduled',
} as const;

type _PaymentStatus = typeof _PAYMENT_STATUS[keyof typeof _PAYMENT_STATUS];

/**
 * Relief backend errors use the standard error_response() envelope:
 * { status: 'error', code, message, details? }. Older/raw errors may use `detail`.
 * Prefer the human `message`, falling back through `detail` to a caller default.
 */
interface ReliefErrorEnvelope {
    code?: string;
    message?: string;
    detail?: string;
    details?: unknown;
}

function reliefErrorMessage(errorData: unknown, fallback: string): string {
    const e = (errorData ?? {}) as ReliefErrorEnvelope;
    return e.message ?? e.detail ?? fallback;
}

/**
 * Payment display configuration.
 * Centralize magic numbers for easier maintenance.
 */
const PAYMENT_DISPLAY_CONFIG = {
    /** Maximum due-soon payments per order (prevents overwhelming daily payers) */
    MAX_DUE_SOON_PER_ORDER: 2,
    /** Maximum upcoming payments per order (preview only) */
    MAX_UPCOMING_PER_ORDER: 1,
    /** Global cap on total displayed payments */
    MAX_TOTAL_PAYMENTS: 12,
    /** Threshold in days for "due soon" classification */
    DUE_SOON_THRESHOLD_DAYS: 7,
} as const;

export class OrderStore {
    // State
    orders: Order[] = [];
    currentOrder: Order | null = null;
    userOrders: Order[] = [];
    pendingOrders: Order[] = [];
    ordersRequiringPayment: Order[] = [];
    overdueOrders: Order[] = [];
    upcomingPayments: UpcomingPayment[] = [];
    private dashboardCacheGeneration = 0;

    // Checkout session state
    currentCheckoutSession: CheckoutSessionResult | null = null;
    isCreatingCheckoutSession = false;
    isInitializingPayment = false;
    paymentInitialization: any = null;

    // Cancellation state
    cancellationPreview: CancellationPreview | null = null;
    isLoadingCancellationPreview = false;
    isRequestingCancellation = false;

    // Payment Relief state
    reliefPreview: ReliefPreview | null = null;
    isLoadingReliefPreview = false;
    isConfirmingRelief = false;
    private reliefPreviewRequestSequence = 0;

    // Delinquency Catch-Up Recovery state (customer already behind)
    recoveryPreview: RecoveryPreview | null = null;
    isLoadingRecoveryPreview = false;
    isAcceptingRecovery = false;
    private recoveryPreviewRequestSequence = 0;

    // Redemption state
    regions: Region[] = [];
    collectionPoints: CollectionPoint[] = [];
    isLoadingRegions = false;
    isLoadingCollectionPoints = false;
    isSettingDeliveryPreference = false;

    isLoading = false;
    isCreating = false;
    error: string | null = null;
    currentFilters: OrderFiltersRequest = {};

    constructor(
        private readonly createOrderUseCase: CreateOrderUseCase,
        private readonly getOrdersUseCase: GetOrdersUseCase,
        private readonly createCheckoutSessionUseCase: CreateCheckoutSessionUseCase,
        private readonly initializePaymentUseCase: InitializePaymentUseCase,
        private readonly checkoutSessionService: CheckoutSessionService,
        private readonly paymentService: ApiPaymentService
    ) {
        makeAutoObservable(this);
    }

    // Computed values
    get dashboardCacheVersion(): number {
        return this.dashboardCacheGeneration;
    }

    get hasOrders(): boolean {
        return this.orders.length > 0;
    }

    get hasUserOrders(): boolean {
        return this.userOrders.length > 0;
    }

    get hasPendingOrders(): boolean {
        return this.pendingOrders.length > 0;
    }

    get hasOrdersRequiringPayment(): boolean {
        return this.ordersRequiringPayment.length > 0;
    }

    get hasOverdueOrders(): boolean {
        return this.overdueOrders.length > 0;
    }

    get isOrderLoaded(): boolean {
        return this.currentOrder !== null;
    }

    get totalOrderValue(): number {
        return this.userOrders.reduce((total, order) => total + Number(order.product.price), 0);
    }

    get totalOutstandingBalance(): number {
        return this.userOrders.reduce((total, order) => total + Number(order.pss_plan?.payments_remaining ?? 0), 0);
    }

    // Checkout session computed values
    get hasActiveCheckoutSession(): boolean {
        return this.currentCheckoutSession !== null;
    }

    get checkoutSessionExpired(): boolean {
        if (!this.currentCheckoutSession) return false;
        return new Date(this.currentCheckoutSession.expiresAt) < new Date();
    }

    get checkoutSessionStatus(): string {
        return this.currentCheckoutSession?.status || 'none';
    }

    get totalCheckoutAmount(): string {
        return this.currentCheckoutSession?.paymentBreakdown.totalAmount || '0';
    }

    get amountDueNow(): string {
        return this.currentCheckoutSession?.paymentBreakdown.amountDueNow ||
            this.currentCheckoutSession?.paymentBreakdown.totalAmount || '0';
    }

    get remainingBalance(): string {
        return this.currentCheckoutSession?.paymentBreakdown.remainingBalance || '0';
    }

    get monthlyContribution(): string {
        return this.currentCheckoutSession?.paymentBreakdown.monthlyContribution || '0';
    }

    get hasUpcomingPayments(): boolean {
        return this.upcomingPayments.length > 0;
    }

    get upcomingPaymentsThisWeek(): any[] {
        const oneWeekFromNow = new Date();
        oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);

        return this.upcomingPayments.filter(payment => {
            const dueDate = new Date(payment.due_date);
            return dueDate <= oneWeekFromNow;
        });
    }

    /**
     * Get all overdue payments.
     * Uses days_until_due (negative = overdue) for reliable detection.
     * Backend already filters out completed payments, so no status filter needed.
     */
    get overduePayments(): UpcomingPayment[] {
        return this.upcomingPayments.filter(payment => payment.days_until_due < 0);
    }

    /**
     * Count of overdue payments.
     * Backend only returns actionable payments (DUE, OVERDUE, FAILED),
     * so no additional status filtering needed.
     */
    get overduePaymentsCount(): number {
        return this.upcomingPayments.filter(p => p.days_until_due < 0).length;
    }

    /**
     * Count of payments due within the next 7 days (but not overdue).
     */
    get dueThisWeekCount(): number {
        return this.upcomingPayments.filter(
            p => p.days_until_due >= 0 &&
                 p.days_until_due <= PAYMENT_DISPLAY_CONFIG.DUE_SOON_THRESHOLD_DAYS
        ).length;
    }

    /**
     * Intelligently filtered and prioritized upcoming payments.
     *
     * Algorithm:
     * 1. Bucket payments by urgency (overdue → due_soon → scheduled)
     * 2. Limit per-order items to prevent overwhelming users
     * 3. Sort by priority, then by due date
     * 4. Cap total items for UI manageability
     *
     * Note: Backend already filters out completed payments (PAID, WAIVED),
     * so all returned payments are actionable.
     */
    get filteredUpcomingPayments(): UpcomingPayment[] {
        const { DUE_SOON_THRESHOLD_DAYS, MAX_DUE_SOON_PER_ORDER,
                MAX_UPCOMING_PER_ORDER, MAX_TOTAL_PAYMENTS } = PAYMENT_DISPLAY_CONFIG;

        // No status filter needed - backend returns only actionable payments
        // (overdue, due_soon, scheduled). The previous filter `status === 'scheduled'`
        // incorrectly excluded overdue and due_soon payments.
        const payments = this.upcomingPayments;

        // Bucket payments by priority using days_until_due
        const overdue = payments.filter(p => p.days_until_due < 0);
        const dueSoon = payments.filter(
            p => p.days_until_due >= 0 && p.days_until_due <= DUE_SOON_THRESHOLD_DAYS
        );
        const upcoming = payments.filter(p => p.days_until_due > DUE_SOON_THRESHOLD_DAYS);

        // For due-soon: limit per order (prevents daily payers seeing 7 items)
        // Sort by days_until_due first to keep the most urgent ones
        const sortedDueSoon = [...dueSoon].sort((a, b) => a.days_until_due - b.days_until_due);
        const dueSoonByOrder = new Map<string, UpcomingPayment[]>();
        sortedDueSoon.forEach(p => {
            const existing = dueSoonByOrder.get(p.order_id) || [];
            if (existing.length < MAX_DUE_SOON_PER_ORDER) {
                dueSoonByOrder.set(p.order_id, [...existing, p]);
            }
        });
        const limitedDueSoon = Array.from(dueSoonByOrder.values()).flat();

        // For upcoming: limit per order (preview only)
        const upcomingByOrder = new Map<string, UpcomingPayment[]>();
        upcoming.forEach(p => {
            const existing = upcomingByOrder.get(p.order_id) || [];
            if (existing.length < MAX_UPCOMING_PER_ORDER) {
                upcomingByOrder.set(p.order_id, [...existing, p]);
            }
        });
        const limitedUpcoming = Array.from(upcomingByOrder.values()).flat();

        // Combine and sort
        const result = [...overdue, ...limitedDueSoon, ...limitedUpcoming]
            .sort((a, b) => {
                // Primary: priority bucket (overdue first)
                const getPriority = (p: UpcomingPayment) =>
                    p.days_until_due < 0 ? 0 : p.days_until_due <= DUE_SOON_THRESHOLD_DAYS ? 1 : 2;
                const priorityDiff = getPriority(a) - getPriority(b);
                if (priorityDiff !== 0) return priorityDiff;
                // Secondary: soonest first
                return a.days_until_due - b.days_until_due;
            });

        // Global cap
        return result.slice(0, MAX_TOTAL_PAYMENTS);
    }

    get actionableBadgeInfo(): { text: string; count: number; show: boolean } {
        const overdueCount = this.overduePaymentsCount;
        const dueThisWeek = this.dueThisWeekCount;

        // Priority 1: Overdue
        if (overdueCount > 0) {
            return {
                text: overdueCount === 1 ? '1 payment overdue' : `${overdueCount} payments overdue`,
                count: overdueCount,
                show: true,
            };
        }

        // Priority 2: Due this week
        if (dueThisWeek > 0) {
            const soonest = this.upcomingPayments.find(p => p.days_until_due >= 0);
            const days = soonest?.days_until_due ?? 0;

            // If only 1 payment, be specific about when (badge shows count, text describes timing)
            if (dueThisWeek === 1) {
                const text = days === 0 ? 'Payment due today' :
                    days === 1 ? 'Payment due tomorrow' :
                    `Payment due in ${days} days`;
                return { text, count: 1, show: true };
            }

            // Multiple payments - badge shows count, text says "due this week"
            return { text: 'Payments due this week', count: dueThisWeek, show: true };
        }

        // Nothing urgent - HIDE the banner
        return { text: '', count: 0, show: false };
    }

    get upcomingPaymentsSummary(): { thisWeekAmount: number; thisMonthAmount: number; currency: string } {
        const thisWeek = this.upcomingPayments
            .filter(p => p.days_until_due >= 0 && p.days_until_due <= 7)
            .reduce((sum, p) => {
                const amount = parseFloat(p.amount);
                return sum + (isNaN(amount) ? 0 : amount);
            }, 0);

        const thisMonth = this.upcomingPayments
            .filter(p => p.days_until_due >= 0 && p.days_until_due <= 30)
            .reduce((sum, p) => {
                const amount = parseFloat(p.amount);
                return sum + (isNaN(amount) ? 0 : amount);
            }, 0);

        const currency = this.upcomingPayments[0]?.currency || 'GHS';

        return { thisWeekAmount: thisWeek, thisMonthAmount: thisMonth, currency };
    }

    // Actions
    async createOrder(request: CreateOrderRequest, userId: string): Promise<void> {
        this.setCreating(true);
        this.setError(null);

        try {
            const orderId = `order_${Date.now()}`;

            const result = await this.createOrderUseCase.execute({
                orderId,
                userId,
                ...request
            });

            runInAction(() => {
                this.currentOrder = result.order;
                // Add to user orders if they exist
                if (this.userOrders.length > 0) {
                    this.userOrders.unshift(this.currentOrder);
                }
            });

        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to create order');
            throw error;
        } finally {
            this.setCreating(false);
        }
    }

    async loadOrderById(id: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const order = await this.getOrdersUseCase.getById(id);

            runInAction(() => {
                this.currentOrder = order;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load order');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async loadOrderByOrderNo(orderNo: number): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const order = await this.getOrdersUseCase.getByOrderNo(orderNo);

            runInAction(() => {
                this.currentOrder = order
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load order');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async loadUserOrders(
        userId: string,
        filters?: OrderFiltersRequest,
        options?: LoadUserOrdersOptions
    ): Promise<void> {
        const generation = this.dashboardCacheGeneration;
        this.setLoading(true);
        this.setError(null);

        try {
            const pageSize = filters?.limit ?? 20;
            const initialOffset = filters?.offset ?? 0;
            const visibilityMatcher = options?.visibilityMatcher;
            const minimumVisibleCount = options?.minimumVisibleCount ?? 0;
            const maxPages = options?.maxPages ?? 25;

            const orders: Order[] = [];
            const seenOrderIds = new Set<string>();
            let currentOffset = initialOffset;
            let fetchedPages = 0;

            while (true) {
                const page = await this.getOrdersUseCase.getByUserId(userId, {
                    ...(filters || {}),
                    limit: pageSize,
                    offset: currentOffset,
                } as any);

                for (const order of page) {
                    if (seenOrderIds.has(order.id)) {
                        continue;
                    }

                    seenOrderIds.add(order.id);
                    orders.push(order);
                }

                const visibleCount = visibilityMatcher
                    ? orders.filter(visibilityMatcher).length
                    : orders.length;
                const pageExhausted = page.length < pageSize;
                const hasEnoughVisibleOrders =
                    !visibilityMatcher || visibleCount >= minimumVisibleCount;
                const reachedPageLimit = fetchedPages >= maxPages - 1;

                if (pageExhausted || hasEnoughVisibleOrders || reachedPageLimit) {
                    break;
                }

                currentOffset += pageSize;
                fetchedPages += 1;
            }

            runInAction(() => {
                if (generation !== this.dashboardCacheGeneration) return;
                this.userOrders = orders;
                this.currentFilters = filters || {};
            });
        } catch (error) {
            if (generation === this.dashboardCacheGeneration) {
                this.setError(error instanceof Error ? error.message : 'Failed to load user orders');
            }
        } finally {
            if (generation === this.dashboardCacheGeneration) this.setLoading(false);
        }
    }

    async loadAllOrders(filters?: OrderFiltersRequest): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getAll(filters as any);

            runInAction(() => {
                this.orders = orders;
                this.currentFilters = filters || {};
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load orders');
        } finally {
            this.setLoading(false);
        }
    }

    async loadUserOrderHistory(userId: string, limit: number = 10): Promise<void> {
        const generation = this.dashboardCacheGeneration;
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getUserOrderHistory(userId, limit);

            runInAction(() => {
                if (generation !== this.dashboardCacheGeneration) return;
                this.userOrders = orders;
            });
        } catch (error) {
            if (generation === this.dashboardCacheGeneration) {
                this.setError(error instanceof Error ? error.message : 'Failed to load order history');
            }
        } finally {
            if (generation === this.dashboardCacheGeneration) this.setLoading(false);
        }
    }

    async loadPendingOrders(userId?: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getPendingOrders(userId);

            runInAction(() => {
                this.pendingOrders = orders;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load pending orders');
        } finally {
            this.setLoading(false);
        }
    }

    async loadOrdersRequiringPayment(userId: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getOrdersRequiringPayment(userId);

            runInAction(() => {
                this.ordersRequiringPayment = orders;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load orders requiring payment');
        } finally {
            this.setLoading(false);
        }
    }

    async loadOverdueOrders(userId?: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getOverdueOrders(userId);

            runInAction(() => {
                this.overdueOrders = orders;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load overdue orders');
        } finally {
            this.setLoading(false);
        }
    }

    // Checkout session actions
    async createCheckoutSession(data: CheckoutSessionData, userId: string): Promise<void> {
        this.isCreatingCheckoutSession = true;
        this.setError(null);

        try {
            const result = await this.createCheckoutSessionUseCase.execute({
                productId: data.productId,
                quantity: data.quantity,
                userId,
                paymentMethod: data.paymentMethod,
                paymentPlan: data.paymentPlan,
                deliveryOption: data.deliveryOption,
                deliveryAddressId: data.deliveryAddressId
            });

            runInAction(() => {
                this.currentCheckoutSession = result;
            });
            trackConsumerEvent({
                eventType: 'checkout_started',
                checkoutSessionId: result.sessionId,
                properties: {
                    product_id: data.productId,
                    checkout_session_id: result.sessionId || '',
                    plan_type: data.paymentPlan?.frequency || data.paymentMethod || 'unknown',
                    amount: Number(result.paymentBreakdown?.amountDueNow || 0),
                    currency: result.paymentBreakdown?.currency || 'GHS',
                },
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to create checkout session');
            throw error;
        } finally {
            this.isCreatingCheckoutSession = false;
        }
    }

    async initializePayment(intent: InitializePaymentParams['intent'], phoneNumber?: string, _amount?: string): Promise<void> {
        if (!this.currentCheckoutSession) {
            throw new Error('No active checkout session');
        }

        this.isInitializingPayment = true;
        this.setError(null);

        try {
            const result = await this.initializePaymentUseCase.execute({
                paymentMethod: "", paymentProvider: "", userId: "",
                intent,
                context: {
                    checkoutSessionId: this.currentCheckoutSession.sessionId,
                    orderId: this.currentCheckoutSession.orderId,
                },
                // provider: 'mtn_momo',
                // method: 'mobile_money',
                phoneNumber: phoneNumber || '',
                // amount: amount || this.currentCheckoutSession.paymentBreakdown.amountDueNow,
                idempotencyKey: `payment_${Date.now()}`
            });

            runInAction(() => {
                this.paymentInitialization = result;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to initialize payment');
            throw error;
        } finally {
            this.isInitializingPayment = false;
        }
    }

    async loadUpcomingPayments(userId: string): Promise<void> {
        const generation = this.dashboardCacheGeneration;
        this.setLoading(true);
        this.setError(null);

        try {
            const payments = await this.paymentService.getUpcomingPayments(userId);

            runInAction(() => {
                if (generation !== this.dashboardCacheGeneration) return;
                this.upcomingPayments = payments.results;
            });
        } catch (error) {
            if (generation === this.dashboardCacheGeneration) {
                this.setError(error instanceof Error ? error.message : 'Failed to load upcoming payments');
            }
        } finally {
            if (generation === this.dashboardCacheGeneration) this.setLoading(false);
        }
    }

    clearCheckoutSession(): void {
        this.currentCheckoutSession = null;
        this.paymentInitialization = null;
    }

    // Cancellation actions

    /**
     * Load cancellation preview for an order.
     * Shows fee estimate, reversal window status, and allowed refund types.
     */
    async loadCancellationPreview(orderId: string): Promise<CancellationPreview> {
        runInAction(() => {
            this.isLoadingCancellationPreview = true;
            this.error = null;
        });

        try {
            const response = await fetch(`/api/proxy/v1/orders/${orderId}/cancellation-preview`, {
                cache: 'no-store',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || 'Failed to load cancellation preview');
            }

            const preview = await response.json() as CancellationPreview;

            runInAction(() => {
                this.cancellationPreview = preview;
            });

            return preview;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load cancellation preview';
            runInAction(() => {
                this.error = errorMessage;
            });
            throw error;
        } finally {
            runInAction(() => {
                this.isLoadingCancellationPreview = false;
            });
        }
    }

    /**
     * Request cancellation for an order.
     * Submits cancellation request with reason and refund type preference.
     */
    async requestCancellation(
        orderId: string,
        reason: string,
        refundType?: 'store_credit' | 'monetary',
        notes?: string
    ): Promise<CancellationResponse> {
        runInAction(() => {
            this.isRequestingCancellation = true;
            this.error = null;
        });

        try {
            const response = await fetch(`/api/proxy/v1/orders/${orderId}/cancel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reason,
                    ...(notes ? { notes } : {}),
                    ...(refundType ? { refund_type: refundType } : {}),
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || 'Failed to cancel order');
            }

            const result = await response.json() as CancellationResponse;

            runInAction(() => {
                this.invalidateCancelledOrder(orderId);
            });

            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to cancel order';
            runInAction(() => {
                this.error = errorMessage;
            });
            throw error;
        } finally {
            runInAction(() => {
                this.isRequestingCancellation = false;
            });
        }
    }

    /**
     * Clear cancellation state.
     */
    clearCancellationState(): void {
        this.cancellationPreview = null;
        this.error = null;
    }

    // Payment Relief actions

    /**
     * Load the relief preview for an order's payment plan.
     * Returns eligibility, the recommended option, and alternatives.
     */
    async loadReliefPreview(paymentPlanId: string, source: ReliefSource = 'order_detail'): Promise<ReliefPreview> {
        const requestId = this.reliefPreviewRequestSequence + 1;
        runInAction(() => {
            this.reliefPreviewRequestSequence = requestId;
            this.isLoadingReliefPreview = true;
            this.error = null;
        });

        try {
            const USE_STUB = process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && window.__RELIEF_API_STUB__;
            let preview: ReliefPreview;

            if (USE_STUB) {
                const { STUB_RELIEF_PREVIEW } = await import('@/shared/stubs/reliefPreviewStub');
                preview = { ...STUB_RELIEF_PREVIEW, source };
            } else {
                const response = await fetch(
                    `/api/proxy/v1/payment-plans/${encodeURIComponent(paymentPlanId)}/relief/preview?source=${encodeURIComponent(source)}`,
                    { cache: 'no-store' },
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(reliefErrorMessage(errorData, 'Failed to load relief options'));
                }
                preview = (await response.json()) as ReliefPreview;
            }

            if (requestId === this.reliefPreviewRequestSequence) {
                runInAction(() => {
                    this.reliefPreview = preview;
                });
            }

            return preview;
        } catch (err) {
            if (requestId === this.reliefPreviewRequestSequence) {
                runInAction(() => {
                    this.error = err instanceof Error ? err.message : 'Failed to load relief options';
                });
            }
            throw err;
        } finally {
            if (requestId === this.reliefPreviewRequestSequence) {
                runInAction(() => {
                    this.isLoadingReliefPreview = false;
                });
            }
        }
    }

    /**
     * Confirm the selected (or recommended) relief option.
     */
    async confirmRelief(
        paymentPlanId: string,
        optionId?: string,
        source: ReliefSource = 'order_detail',
    ): Promise<ReliefConfirmResponse> {
        runInAction(() => {
            this.isConfirmingRelief = true;
            this.error = null;
        });

        try {
            const USE_STUB = process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && window.__RELIEF_API_STUB__;
            let result: ReliefConfirmResponse;

            if (USE_STUB) {
                const { STUB_RELIEF_CONFIRM } = await import('@/shared/stubs/reliefPreviewStub');
                result = STUB_RELIEF_CONFIRM;
            } else {
                const response = await fetch(`/api/proxy/v1/payment-plans/${encodeURIComponent(paymentPlanId)}/relief/accept`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ option_id: optionId ?? null, source }),
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(reliefErrorMessage(errorData, 'Failed to confirm relief'));
                }
                result = (await response.json()) as ReliefConfirmResponse;
            }

            runInAction(() => {
                // Accepted reliefs and support-review reliefs are both persisted backend states.
                // The drawer distinguishes them by status/type so support review renders as
                // pending review, not as the normal "auto-debit paused" success screen.
                if (
                    this.reliefPreview
                    && result.relief
                    && (result.status === 'accepted' || result.status === 'support_review_requested')
                ) {
                    this.reliefPreview.active_relief = result.relief;
                    this.reliefPreview.eligible = false;
                    this.reliefPreview.ineligible_reasons = [
                        result.status === 'support_review_requested'
                            ? 'relief_support_review_pending'
                            : 'relief_already_active',
                    ];
                }
            });

            return result;
        } catch (err) {
            runInAction(() => {
                this.error = err instanceof Error ? err.message : 'Failed to confirm relief';
            });
            throw err;
        } finally {
            runInAction(() => {
                this.isConfirmingRelief = false;
            });
        }
    }

    /**
     * Resume auto-debit early (cancel active relief).
     */
    async resumeAutoDebit(paymentPlanId: string): Promise<void> {
        try {
            const USE_STUB = process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && window.__RELIEF_API_STUB__;
            if (!USE_STUB) {
                const response = await fetch(`/api/proxy/v1/payment-plans/${encodeURIComponent(paymentPlanId)}/relief/resume`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(reliefErrorMessage(errorData, 'Failed to resume auto-debit'));
                }
            }

            runInAction(() => {
                if (this.reliefPreview) {
                    this.reliefPreview.active_relief = null;
                    this.reliefPreview.eligible = true;
                    this.reliefPreview.ineligible_reasons = [];
                }
            });
        } catch (err) {
            runInAction(() => {
                this.error = err instanceof Error ? err.message : 'Failed to resume auto-debit';
            });
            throw err;
        }
    }

    /**
     * Clear relief state.
     */
    clearReliefState(): void {
        this.reliefPreviewRequestSequence += 1;
        this.reliefPreview = null;
        this.isLoadingReliefPreview = false;
        this.error = null;
    }

    /**
     * Load the catch-up recovery preview for a delinquent plan.
     * Mirrors loadReliefPreview; backed by recovery_service.py's preview contract.
     */
    async loadRecoveryPreview(paymentPlanId: string): Promise<RecoveryPreview> {
        const requestId = this.recoveryPreviewRequestSequence + 1;
        runInAction(() => {
            this.recoveryPreviewRequestSequence = requestId;
            this.isLoadingRecoveryPreview = true;
            this.error = null;
        });

        try {
            const USE_STUB = process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && window.__RECOVERY_API_STUB__;
            let preview: RecoveryPreview;

            if (USE_STUB) {
                const { STUB_RECOVERY_PREVIEW } = await import('@/shared/stubs/recoveryPreviewStub');
                preview = {
                    ...STUB_RECOVERY_PREVIEW,
                    payment_plan_id: paymentPlanId,
                };
            } else {
                const response = await fetch(
                    `/api/proxy/v1/payment-plans/${encodeURIComponent(paymentPlanId)}/delinquency/recovery/preview`,
                    { cache: 'no-store' },
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(reliefErrorMessage(errorData, 'Failed to load catch-up options'));
                }
                preview = (await response.json()) as RecoveryPreview;
            }

            if (requestId === this.recoveryPreviewRequestSequence) {
                runInAction(() => {
                    this.recoveryPreview = preview;
                });
            }

            return preview;
        } catch (err) {
            if (requestId === this.recoveryPreviewRequestSequence) {
                runInAction(() => {
                    this.error = err instanceof Error ? err.message : 'Failed to load catch-up options';
                });
            }
            throw err;
        } finally {
            if (requestId === this.recoveryPreviewRequestSequence) {
                runInAction(() => {
                    this.isLoadingRecoveryPreview = false;
                });
            }
        }
    }

    /**
     * Accept a catch-up recovery option (pay_now / spread / move_to_end).
     * `amount` applies to pay_now/partial only. Returns the schedule effect for
     * the confirmation screen; `payment_required` means route to payment.
     */
    async acceptRecovery(
        paymentPlanId: string,
        optionId: string,
        amount?: string,
    ): Promise<RecoveryAcceptResponse> {
        runInAction(() => {
            this.isAcceptingRecovery = true;
            this.error = null;
        });

        try {
            const USE_STUB = process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && window.__RECOVERY_API_STUB__;
            let result: RecoveryAcceptResponse;

            if (USE_STUB) {
                const stub = await import('@/shared/stubs/recoveryPreviewStub');
                if (optionId === 'pay_now') result = stub.STUB_RECOVERY_ACCEPT_PAY_NOW;
                else if (optionId.startsWith('move_to_end')) result = stub.STUB_RECOVERY_ACCEPT_MOVE_TO_END;
                else result = stub.STUB_RECOVERY_ACCEPT_SPREAD;
                result = normalizeStubRecoveryAcceptResponse({
                    response: result,
                    paymentPlanId,
                    amount,
                });
            } else {
                const body: { option_id: string; amount?: string } = { option_id: optionId };
                if (amount !== undefined) body.amount = amount;
                const response = await fetch(
                    `/api/proxy/v1/payment-plans/${encodeURIComponent(paymentPlanId)}/delinquency/recovery/accept`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    },
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(reliefErrorMessage(errorData, 'Failed to apply your catch-up option'));
                }
                result = (await response.json()) as RecoveryAcceptResponse;
            }

            return normalizeRecoveryAcceptResponse(result);
        } catch (err) {
            runInAction(() => {
                this.error = err instanceof Error ? err.message : 'Failed to apply your catch-up option';
            });
            throw err;
        } finally {
            runInAction(() => {
                this.isAcceptingRecovery = false;
            });
        }
    }

    /**
     * Clear catch-up recovery state.
     */
    clearRecoveryState(): void {
        this.recoveryPreviewRequestSequence += 1;
        this.recoveryPreview = null;
        this.isLoadingRecoveryPreview = false;
        this.error = null;
    }

    // Redemption actions

    /**
     * Load available regions for collection points.
     */
    async loadRegions(): Promise<Region[]> {
        runInAction(() => {
            this.isLoadingRegions = true;
            this.error = null;
        });

        try {
            const response = await fetch(`/api/proxy/v1/collection-points/regions`, {
                cache: 'no-store',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || 'Failed to load regions');
            }

            const data = await response.json();
            const regions = Array.isArray(data) ? data : (data.results || []);

            runInAction(() => {
                this.regions = regions;
            });

            return regions;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load regions';
            runInAction(() => {
                this.error = errorMessage;
            });
            throw error;
        } finally {
            runInAction(() => {
                this.isLoadingRegions = false;
            });
        }
    }

    /**
     * Load collection points for a specific region.
     * Backend accepts both region codes (e.g., 'GA') and labels (e.g., 'Greater Accra').
     */
    async loadCollectionPoints(region: string): Promise<CollectionPoint[]> {
        runInAction(() => {
            this.isLoadingCollectionPoints = true;
            this.error = null;
        });

        try {
            const response = await fetch(`/api/proxy/v1/collection-points?region=${encodeURIComponent(region)}`, {
                cache: 'no-store',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || 'Failed to load collection points');
            }

            const data = await response.json();
            const points = Array.isArray(data) ? data : (data.results || []);

            runInAction(() => {
                this.collectionPoints = points;
            });

            return points;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load collection points';
            runInAction(() => {
                this.error = errorMessage;
            });
            throw error;
        } finally {
            runInAction(() => {
                this.isLoadingCollectionPoints = false;
            });
        }
    }

    /**
     * Set delivery preference for an order.
     * This is KYC-gated on the backend - will fail if user doesn't have STANDARD KYC.
     */
    async setDeliveryPreference(
        orderId: string,
        input:
            | {
                  method: 'COLLECTION_POINT';
                  collectionPoint: {
                      id: string;
                      code: string;
                      name: string;
                      region: string;
                      city: string;
                  };
                  pickupNote?: string;
              }
            | {
                  method: 'HOME_DELIVERY';
                  deliveryNote: string;
                  deliveryPhone?: string;
              }
    ): Promise<Order> {
        runInAction(() => {
            this.isSettingDeliveryPreference = true;
            this.error = null;
        });

        try {
            const body: Record<string, unknown> = {
                method: input.method,
            };

            if (input.method === 'COLLECTION_POINT') {
                body.delivery_preferences = {
                    collection_point: input.collectionPoint,
                };
                if (input.pickupNote) {
                    body.pickup_note = input.pickupNote;
                }
            } else {
                body.delivery_note = input.deliveryNote;
                if (input.deliveryPhone) {
                    body.delivery_phone = input.deliveryPhone;
                }
            }

            const response = await fetch(`/api/proxy/v1/orders/${orderId}/delivery-preference`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                // Check for KYC gate error
                const errorCode = String((errorData as any)?.code || '');
                if (errorCode === 'fulfilment_checklist_incomplete') {
                    const details = (errorData as any)?.details;
                    const checklist = details?.fulfilment_checklist;
                    const kycError = new Error('KYC_REQUIRED') as Error & {
                        checklist?: unknown;
                        details?: unknown;
                    };
                    kycError.checklist = checklist;
                    kycError.details = details;
                    throw kycError;
                }
                throw new Error(errorData.detail || errorData.message || 'Failed to set delivery preference');
            }

            const updatedOrder = await response.json() as Order;
            // NOTE: `redemption_submitted` + `redemption_success` are tracked server-side so we count
            // successful state changes (and avoid client-side duplication/retries inflating metrics).

            runInAction(() => {
                // Update current order if it matches
                if (this.currentOrder && this.currentOrder.id === orderId) {
                    this.currentOrder = updatedOrder;
                }
                // Update order in user orders list
                const orderIndex = this.userOrders.findIndex(o => o.id === orderId);
                if (orderIndex !== -1) {
                    this.userOrders[orderIndex] = updatedOrder;
                }
            });

            return updatedOrder;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to set delivery preference';
            runInAction(() => {
                // Avoid surfacing internal sentinel messages to UI. The caller can handle them explicitly.
                this.error = errorMessage === 'KYC_REQUIRED' ? null : errorMessage;
            });
            throw error;
        } finally {
            runInAction(() => {
                this.isSettingDeliveryPreference = false;
            });
        }
    }

    /**
     * Clear redemption state.
     */
    clearRedemptionState(): void {
        this.regions = [];
        this.collectionPoints = [];
        this.error = null;
    }

    // Helper methods
    private invalidateCancelledOrder(orderId: string): void {
        // Cancellation returns order detail, not the dashboard projection. Let
        // the page's canonical readers refill it without restoring older reads.
        this.dashboardCacheGeneration += 1;
        this.isLoading = false;
        if (this.currentOrder?.id === orderId) this.currentOrder = null;
        this.userOrders = this.userOrders.filter(order => order.id !== orderId);
        this.upcomingPayments = this.upcomingPayments.filter(payment => payment.order_id !== orderId);
    }

    setCurrentOrder(order: Order | null): void {
        this.currentOrder = order;
    }

    clearOrders(): void {
        this.dashboardCacheGeneration += 1;
        this.isLoading = false;
        this.orders = [];
        this.userOrders = [];
        this.pendingOrders = [];
        this.ordersRequiringPayment = [];
        this.overdueOrders = [];
        this.currentOrder = null;
        this.upcomingPayments = [];
    }

    getOrdersByStatus(status: OrderStatus): Order[] {
        return this.userOrders.filter(order => order.payment_status === status);
    }

    getOrdersByPaymentType(paymentType: PaymentType): Order[] {
        return this.userOrders.filter(order => order.payment_method === paymentType);
    }

    getCompletedOrders(): Order[] {
        return this.userOrders.filter(order => Number(order.pss_plan?.payments_remaining ?? Number.NaN) === 0);
    }

    getActiveOrders(): Order[] {
        return this.userOrders.filter(order =>
            !order.id &&
            (order.payment_status !== OrderStatus.CANCELLED) &&
            (order.payment_status !== OrderStatus.REFUNDED)
        );
    }

    private setLoading(loading: boolean): void {
        this.isLoading = loading;
    }

    private setCreating(creating: boolean): void {
        this.isCreating = creating;
    }

    private setError(error: string | null): void {
        this.error = error;
    }
}

function normalizeRecoveryAcceptResponse(response: RecoveryAcceptResponse): RecoveryAcceptResponse {
    if (!response.payment_action || !response.relief_id) {
        return response;
    }

    const paymentContext = response.payment_action.payment_context;
    if (paymentContext.recovery_relief_id) {
        return response;
    }

    return {
        ...response,
        payment_action: {
            ...response.payment_action,
            payment_context: {
                ...paymentContext,
                recovery_relief_id: response.relief_id,
            },
        },
    };
}

function normalizeStubRecoveryAcceptResponse({
    response,
    paymentPlanId,
    amount,
}: {
    response: RecoveryAcceptResponse;
    paymentPlanId: string;
    amount?: string;
}): RecoveryAcceptResponse {
    const normalizedAmount = amount ?? response.payment_action?.amount;
    return {
        ...response,
        payment_plan_id: paymentPlanId,
        payment_action: response.payment_action
            ? {
                ...response.payment_action,
                ...(normalizedAmount ? { amount: normalizedAmount } : {}),
                payment_context: {
                    ...response.payment_action.payment_context,
                    payment_plan_id: paymentPlanId,
                    ...(normalizedAmount ? { amount: normalizedAmount } : {}),
                },
            }
            : response.payment_action,
    };
}
